<template>
  <div class="account-details-display">
    <div class="row">
      <div class="col-sm-12">
        <org-logo :org="account.Organization"></org-logo>
        <div class="toolbar text-right">
          <dropdown-input
            label="Account Actions"
            label-icon="fa-user"
            :dropright="true"
            :actions="userActions(account, loggedInUser)"
            @action="executeAction($event, account)">
          </dropdown-input>
          <dropdown-input
            v-if="isFinanceAdmin"
            label="Billing Actions"
            label-icon="fa-dollar-sign"
            :dropright="true"
            :actions="billingActions(account, loggedInUser)"
            @action="executeAction($event, account)">
          </dropdown-input>
          <a target="_blank" class="btn btn-info" :href="'https://dashboard.stripe.com/customers/' + account.stripeAccount">
            <span class="fab fa-cc-stripe fa-fw"></span> View Customer On Stripe
          </a>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <accordian-display
          :icon="'fa-user'"
          :title="'Personal Details'"
          :defaultOpen="true">
          <tr>
            <td class="font-weight-bold">Username</td>
            <td>
              {{ account.username }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Member Status</td>
            <td>
              <span class="badge" :class="membershipStatus(account).label">
                {{ membershipStatus(account).text }}
              </span>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Account Type</td>
            <td>
              {{ account.AccountType.name }} <span v-if="account.Master">(Driver)</span>
            </td>
          </tr>
          <tr v-if="account.isMasterAccount && account.AccountType.maxAccounts > 0 && account.Drivers">
            <td class="font-weight-bold">Drivers</td>
            <td>
              {{ account.Drivers.length }}
            </td>
          </tr>
          <tr v-if="account.Master">
            <td class="font-weight-bold">Master Account</td>
            <td>
              <router-link :to="{ path: '/membership/members/' + account.Master.uuid }">{{ account.Master.fullName }}</router-link>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Total Account Sales</td>
            <td>
              {{ account.totalAccountSales | to_dollar_amount }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Total Account Payments</td>
            <td>
              {{ account.totalAccountPayments | to_dollar_amount }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Driving Credits Given</td>
            <td>
              {{ account.totalCreditsGiven | to_dollar_amount }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Current Driving Credits</td>
            <td>
              <span :class="{ 'text-danger' : account.credits < 0 }">
                {{ account.credits | to_dollar_amount_neg }}
              </span>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Full Name</td>
            <td>
              {{ account.fullName }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Email</td>
            <td>
              <a :href="`mailto:${account.email}`">
                {{ account.email }}
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Registered Date</td>
            <td>
              {{ account.registeredDate | short_date_format }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Approval Date</td>
            <td>
              {{ account.approvalDate | short_date_format }}
            </td>
          </tr>
          <tr v-if="account.isMasterAccount">
            <td class="font-weight-bold">Membership Last Paid</td>
            <td>
              <span :class="{ 'text-danger': account.overdue }" v-if="account.AccountType.monthlyFee || account.AccountType.annualFee">{{ account.membershipLastPaid | short_date_format }}</span>
              <span v-else>No membership fees</span>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Smart Card</td>
            <td>
              <span class="badge badge-success" v-if="account.Smartcard && account.Smartcard.uuid">
                <i class="fa fa-id-card"></i> {{ account.Smartcard.cardNumber }}
              </span>
              <span class="badge badge-danger" v-else>
                <i class="fa fa-id-card"></i> No Smartcard Assigned
              </span>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Address</td>
            <td>
              {{ account.address }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Suburb</td>
            <td>
              {{ account.suburb }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Postcode</td>
            <td>
              {{ account.postcode }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">State</td>
            <td>
              {{ account.state }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Proof of Address</td>
            <td>
              <a v-if="account.ProofOfAddress"
                class="btn btn-primary btn-sm"
                :href="url + account.ProofOfAddress.objectKey"
                target="_blank">
                <span class="fa fa-fw fa-download"></span>
                Download
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Home Phone</td>
            <td>
              <a :href="`tel:${account.homePhone}`">
                {{ account.homePhone }}
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Mobile Phone</td>
            <td>
              <a :href="`tel:${account.mobilePhone}`">
                {{ account.mobilePhone }}
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Work Phone</td>
            <td>
              <a :href="`tel:${account.workPhone}`">
                {{ account.workPhone }}
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Date of Birth</td>
            <td>
              {{ account.dateOfBirth | short_date_format }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Gender</td>
            <td>
              {{ account.gender }}
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-graduation-cap'"
          v-if="account.AccountType.name === 'Student'"
          :title="'Student Details'">
          <tr>
            <td class="font-weight-bold">Proof of Enrolment</td>
            <td>
              <a v-if="account.ProofOfEnrolment"
              class="btn btn-primary btn-sm"
              :href="url + account.ProofOfEnrolment.objectKey"
              target="_blank">
                <span class="fa fa-fw fa-download"></span>
                Download
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Student Expiry</td>
            <td>
              {{ account.studentExpiry | short_date_format }}
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-briefcase'"
          v-if="account.AccountType.name === 'Business'"
          :title="'Business Details'">
          <tr>
            <td class="font-weight-bold">Name</td>
            <td>
              {{ account.businessName }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">ABN</td>
            <td>
              {{ account.businessAbn }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Address</td>
            <td>
              {{ account.businessAddress }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Number of Employees</td>
            <td>
              {{ account.businessEmployees }}
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-certificate'"
          v-if="account.MemberDiscount"
          :title="'Member Discount'">
          <tr>
            <td class="font-weight-bold">Percentage Amount</td>
            <td>
              {{ account.MemberDiscount.percentDiscount }}%
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Expiry</td>
            <td v-if="account.MemberDiscount.expiry">
              {{ account.MemberDiscount.expiry | short_date_format }}
            </td>
            <td v-else>
              No expiry
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Setup By</td>
            <td>
              <router-link :to="{ path: '/membership/members/' + account.MemberDiscount.AdminResponsible.uuid }">
                {{ account.MemberDiscount.AdminResponsible.fullName }}
              </router-link>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Reason</td>
            <td>
              {{ account.MemberDiscount.reason }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Approved By</td>
            <td>
              <router-link v-if="account.MemberDiscount.AdminApproved" :to="{ path: '/membership/members/' + account.MemberDiscount.AdminApproved.uuid }">
                {{ account.MemberDiscount.AdminApproved.fullName }}
              </router-link>
              <em v-else>Awaiting approval</em>
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-credit-card'"
          :title="'Payment Methods'">
          <tr v-if="account.CreditCards.length == 0">
            <td colspan="2" class="bg-danger text-white">
              <span>
                <i class="fa-exclamation-triangle fa"></i> No credit card on file
              </span>
            </td>
          </tr>
          <template v-if="account.CreditCards.length > 0 && !isFinanceAdmin">
            <tr v-for="card in account.CreditCards"
            :key="card.key">
              <td colspan="2">
                <span>
                  <i class="fa-lg" :class="card.icon"></i> {{ card.dropdownLabel }}
                </span>
              </td>
            </tr>
          </template>
          <tr v-if="isFinanceAdmin">
            <td colspan="2">
              <credit-card-input
                :value="account.CreditCards"
                :options="{
                  name: 'creditCard',
                  value: account.CreditCards,
                  fullWidth: true
                }">
              </credit-card-input>
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-ambulance'"
          :title="'Emergency Contact'">
          <tr>
            <td class="font-weight-bold">Full Name</td>
            <td>
              {{ account.emergencyFullName }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Relation</td>
            <td>
              {{ account.emergencyRelation }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Phone</td>
            <td>
              <a :href="`tel:${account.emergencyPhone}`">
                {{ account.emergencyPhone }}
              </a>
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-id-card'"
          :title="'Drivers License'">
          <tr>
            <td class="font-weight-bold">License Number</td>
            <td>
              {{ account.driversLicense }}
            </td>
          </tr>
          <tr v-if="account.driversLicenseIssued">
            <td class="font-weight-bold">State Issued</td>
            <td>
              {{ account.driversLicenseIssued }}
            </td>
          </tr>
          <tr v-if="account.driversLicenseCountry">
            <td class="font-weight-bold">International License</td>
            <td>
              {{ account.driversLicenseCountry }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">License Expiry</td>
            <td>
              {{ account.driversLicenseExpiry | short_date_format }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Drivers License (Front)</td>
            <td>
              <a v-if="account.DriversLicense"
                class="btn btn-primary btn-sm"
                :href="url + account.DriversLicense.objectKey"
                target="_blank">
                <span class="fa fa-fw fa-download"></span>
                Download
              </a>
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Drivers License (Back)</td>
            <td>
              <a v-if="account.DriversLicenseBack"
                class="btn btn-primary btn-sm"
                :href="url + account.DriversLicenseBack.objectKey"
                target="_blank">
                <span class="fa fa-fw fa-download"></span>
                Download
              </a>
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-history'"
          :title="'Drivers History'">
          <tr>
            <td class="font-weight-bold">Years Driving</td>
            <td>
              {{ account.driversYears }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Infringements (past 3 yrs)</td>
            <td>
              {{ account.drivingInfringements }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Accidents (past 3 yrs)</td>
            <td>
              {{ account.accidents }}
            </td>
          </tr>
          <tr>
            <td class="font-weight-bold">Alcohol Related Incidents (past 7 yrs)</td>
            <td>
              {{ account.alcoholRelated ? "Yes" : "No" }}
            </td>
          </tr>
        </accordian-display>
        <accordian-display
          :icon="'fa-paper-plane'"
          :title="'Email Preferences'">
          <tr
          v-for="alert in account.allAlerts"
          :key="alert.uuid">
            <td class="font-weight-bold">{{ alert.name }}</td>
            <td>
              <checkbox-input class="pt-3"
                :options="{ fullWidth: true }"
                :value="account.emailPrefs.includes(alert.uuid)"
                @input="addRemoveAlert(alert)">
              </checkbox-input>
            </td>
          </tr>
        </accordian-display>
      </div>
      <div class="col-md-8">
        <nav-tab :tab-count="5" ref="tabs">
          <!-- Reservation History -->
          <div title="Reservation History" slot="tab1" icon="fa-history">
            <div class="card card-primary">
              <div class="card-header">
                Reservation History
              </div>
              <div class="card-body">
                <search-form
                  ref="reservationSearchForm"
                  :results="userReservations"
                  :total-results="userReservationsTotal"
                  :options="reservationsForm"
                  :columns="userReservationsColumns"
                  :tableDisplay="reservationTableDisplay"
                  @toggleDisplay="reservationTableDisplay = !reservationTableDisplay"
                  :allowToggle="true">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Account Notes -->
          <div title="Notes" slot="tab2" icon="fa-sticky-note">
            <div class="card card-primary">
              <div class="card-header">
                Account Notes
              </div>
              <div class="card-body">
                <search-form
                  ref="accountNoteSearchForm"
                  :results="userNotes"
                  :total-results="userNotesTotal"
                  :options="notesForm">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Billing -->
          <div title="Billing" slot="tab3" icon="fa-dollar-sign">
            <div class="card card-primary">
              <div class="card-header">
                Billing
              </div>
              <div class="card-body">
                <search-form
                  ref="ledgerSearchForm"
                  :results="userLedger"
                  :options="ledgerForm"
                  :columns="userLedgerColumns"
                  :tableDisplay="true">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Files -->
          <div title="Files" slot="tab4" icon="fa-file">
            <div class="card card-primary">
              <div class="card-header">
                Account Files
              </div>
              <div class="card-body">
                <search-form
                  ref="accountFilesSearchForm"
                  :results="userFiles"
                  :total-results="userFilesTotal"
                  :options="filesForm"
                  :columns="userFilesColumns"
                  :tableDisplay="true">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Drivers -->
          <div title="Drivers" slot="tab5" icon="fa-users">
            <div class="card card-primary">
              <div class="card-header">
                Drivers
              </div>
              <div class="card-body">
                <search-form
                  ref="accountFilesSearchForm"
                  :results="userDrivers"
                  :total-results="userDriversTotal"
                  :columns="userDriversColumns"
                  :options="driversForm"
                  :tableDisplay="true">
                </search-form>
              </div>
            </div>
          </div>
        </nav-tab>
      </div>
    </div>
  </div>
</template>

<script>
import CheckboxInput from '@/components/inputs/CheckboxInput'
import AccordianDisplay from '@/components/displays/AccordianDisplay'
import SearchForm from '@/components/SearchForm'
import DropdownInput from '@/components/inputs/DropdownInput'
import CreditCardInput from '@/components/inputs/CreditCardInput'
import OrgLogo from '@/components/OrgLogo'
import ResponsiveTable from '@/components/ResponsiveTable'
import DriverRow from '@/components/rows/Driver'
import LedgerRow from '@/components/rows/Ledger'
import NavTab from '@/components/NavTab'
import {
  ADD_ACCOUNT_NOTE,
  ADD_CREDITS,
  ADD_SMARTCARD,
  BLACKLIST_MEMBER,
  UN_BLACKLIST_MEMBER,
  CHANGE_PASS_USER,
  CHANGE_PLAN_USER,
  CHARGE_ACCOUNT,
  EDIT_APPLICATION,
  EDIT_SMARTCARD,
  GENERATE_MONTHLY_INVOICE,
  INVITE_DRIVER,
  LOAD_USER,
  LOGIN_AS_USER,
  MAKE_ACTIVE,
  MAKE_INACTIVE,
  MAKE_MASTER_ACCOUNT,
  NEW_RESERVATION_USER,
  QUIT_MEMBER,
  REJOIN_MEMBER,
  RESTRICT_MEMBER,
  TOGGLE_ALERT,
  UNASSIGN_SMARTCARD,
  UPLOAD_FILE,
  UNRESTRICT_MEMBER
} from '@/vuex/types'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AccountDetailsDisplay',
  components: {
    AccordianDisplay,
    DropdownInput,
    CheckboxInput,
    CreditCardInput,
    OrgLogo,
    ResponsiveTable,
    DriverRow,
    LedgerRow,
    SearchForm,
    NavTab
  },
  computed: {
    account () {
      return {
        MemberDiscount: {
          AdminResponsible: {}
        },
        AccountType: {},
        ...this.loadedUser,
        ...this.updatedAccount
      }
    },
    reservationsForm () {
      return {
        permalink: 'getUserReservations',
        method: 'post',
        params: {
          accountUuid: this.$route.params.account_id
        }
      }
    },
    driversForm () {
      return {
        permalink: 'getUserDrivers',
        method: 'post',
        params: {
          accountUuid: this.$route.params.account_id
        }
      }
    },
    notesForm () {
      return {
        permalink: 'getUserNotes',
        method: 'post',
        params: {
          accountUuid: this.$route.params.account_id
        }
      }
    },
    ledgerForm () {
      return {
        permalink: 'getLedger',
        method: 'post',
        params: {
          accountUuid: this.$route.params.account_id
        }
      }
    },
    filesForm () {
      return {
        permalink: 'getUserFiles',
        method: 'post',
        params: {
          accountUuid: this.$route.params.account_id
        }
      }
    },
    ...mapGetters([
      'userActions',
      'billingActions',
      'loadedUser',
      'userReservations',
      'userReservationsTotal',
      'userReservationsColumns',
      'userFiles',
      'userFilesTotal',
      'userFilesColumns',
      'userNotes',
      'userLedger',
      'userLedgerColumns',
      'userNotesTotal',
      'loggedInUser',
      'userDriversColumns',
      'userDriversTotal',
      'userDrivers',
      'isFinanceAdmin'
    ])
  },
  data () {
    return {
      reservationTableDisplay: true,
      updatedAccount: {},
      url: 'https://techcar-dev.s3.amazonaws.com/'
    }
  },
  watch: {
    $route () {
      this[LOAD_USER](this.$route.params.account_id)
    }
  },
  mounted () {
    this[LOAD_USER](this.$route.params.account_id)
  },
  methods: {
    addRemoveAlert (alert) {
      let on = this.account.emailPrefs.includes(alert.uuid)
      this.executeAction(TOGGLE_ALERT, { alertData: { alert, on }, accountUuid: this.account.uuid })
    },
    membershipStatus (account) {
      let label = ''
      let text = ''
      let {
        activated,
        restricted,
        memberQuit
      } = account
      if (memberQuit) {
        label = 'badge-warning'
        text = 'Quit'
      } else if (restricted) {
        label = 'badge-danger'
        text = 'Restricted'
      } else if (!activated) {
        label = 'badge-default'
        text = 'Inactive'
      } else {
        label = 'badge-success'
        text = 'Active'
      }
      return { label, text }
    },
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedAccount = result.updated
    },
    ...mapActions([
      ADD_ACCOUNT_NOTE,
      ADD_CREDITS,
      ADD_SMARTCARD,
      BLACKLIST_MEMBER,
      UN_BLACKLIST_MEMBER,
      CHANGE_PASS_USER,
      CHANGE_PLAN_USER,
      CHARGE_ACCOUNT,
      EDIT_APPLICATION,
      EDIT_SMARTCARD,
      GENERATE_MONTHLY_INVOICE,
      INVITE_DRIVER,
      LOAD_USER,
      LOGIN_AS_USER,
      MAKE_ACTIVE,
      MAKE_INACTIVE,
      MAKE_MASTER_ACCOUNT,
      NEW_RESERVATION_USER,
      QUIT_MEMBER,
      REJOIN_MEMBER,
      RESTRICT_MEMBER,
      TOGGLE_ALERT,
      UNASSIGN_SMARTCARD,
      UPLOAD_FILE,
      UNRESTRICT_MEMBER
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/AccountDetailsDisplay.vue